import { useRef, useState, useCallback } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ZoomIn } from "lucide-react";
import { IMAGES } from "@/data/site";
import { Lightbox } from "@/components/site/Lightbox";

const EASE = [0.76, 0, 0.24, 1];

const layouts = [
    "aspect-[4/5]",
    "aspect-[3/4]",
    "aspect-[4/5]",
    "aspect-[3/4]",
    "aspect-[3/4]",
    "aspect-[4/5]",
    "aspect-[3/4]",
    "aspect-[4/5]",
];

const GalleryItem = ({ src, i, onOpen }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const y = useTransform(
        scrollYProgress,
        [0, 1],
        i % 2 === 0 ? ["-6%", "10%"] : ["8%", "-8%"],
    );

    return (
        <motion.button
            ref={ref}
            type="button"
            onClick={() => onOpen(i)}
            data-testid={`gallery-item-${i}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: EASE, delay: (i % 4) * 0.05 }}
            className={`group photo-frame relative w-full overflow-hidden bg-[var(--ink-3)] cursor-zoom-in block ${layouts[i % layouts.length]}`}
        >
            <motion.img
                style={{ y }}
                src={src}
                alt={`Foto ${i + 1} da galeria — clique para ampliar`}
                className="absolute inset-0 h-[115%] w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.05]"
            />
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-[var(--ink)]/0 group-hover:bg-[var(--ink)]/35 transition-colors duration-500" />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <span className="inline-flex items-center gap-2 border border-[var(--sun)] text-[var(--sun)] bg-[var(--ink)]/40 backdrop-blur-sm px-4 py-2 font-body text-[11px] tracking-[0.32em] uppercase">
                    <ZoomIn size={14} />
                    Ampliar
                </span>
            </div>
            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-[9px] tracking-[0.32em] uppercase text-white/80 font-body z-10">
                <span>N°{String(i + 1).padStart(2, "0")}</span>
                <span className="hidden sm:inline">Novo Horizonte</span>
            </div>
        </motion.button>
    );
};

export const Galeria = () => {
    const [openIndex, setOpenIndex] = useState(null);
    const total = IMAGES.gallery.length;

    const open = useCallback((i) => setOpenIndex(i), []);
    const close = useCallback(() => setOpenIndex(null), []);
    const prev = useCallback(
        () => setOpenIndex((i) => (i === null ? null : (i - 1 + total) % total)),
        [total],
    );
    const next = useCallback(
        () => setOpenIndex((i) => (i === null ? null : (i + 1) % total)),
        [total],
    );

    return (
        <section
            id="galeria"
            data-testid="galeria-section"
            className="relative bg-[var(--paper)] text-[var(--ink)] py-24 md:py-32"
        >
            <div className="container-x">
                <div className="grid grid-cols-12 gap-6 mb-16 md:mb-20">
                    <div className="col-span-12 md:col-span-6">
                        <div className="flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-[var(--ink)]/60 font-body">
                            <span className="inline-block h-px w-10 bg-[var(--ink)]/40" />
                            <span>Seção 03</span>
                        </div>
                        <h2
                            data-testid="galeria-title"
                            className="font-display text-[13vw] md:text-[7vw] lg:text-[6vw] mt-6"
                        >
                            <span className="font-editorial italic font-medium text-[var(--ink-2)]">
                                galeria
                            </span>{" "}
                            vivida.
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-5 md:col-start-8 flex items-end">
                        <p className="font-body text-[15px] leading-relaxed text-[var(--ink)]/75 max-w-md">
                            Momentos capturados dentro e fora de campo. Toque
                            em qualquer imagem para ampliar — navegue com as
                            setas do teclado.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                    {IMAGES.gallery.map((src, i) => (
                        <div
                            key={i}
                            className={`${
                                i % 4 === 1 || i % 4 === 2 ? "md:mt-14" : ""
                            }`}
                        >
                            <GalleryItem src={src} i={i} onOpen={open} />
                        </div>
                    ))}
                </div>

                <div className="mt-16 md:mt-20 flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-t border-[var(--ink)]/15 pt-10">
                    <p className="font-editorial italic text-2xl md:text-3xl leading-tight text-[var(--ink)]/80 max-w-xl">
                        Clique nas fotos para ampliar. Toda a galeria pode ser
                        substituída trocando os arquivos em <code>/fotos/</code>.
                    </p>
                    <span className="font-body text-[10px] tracking-[0.32em] uppercase text-[var(--ink)]/50">
                        Arquivo · atualizado periodicamente
                    </span>
                </div>
            </div>

            <Lightbox
                images={IMAGES.gallery}
                index={openIndex}
                onClose={close}
                onPrev={prev}
                onNext={next}
            />
        </section>
    );
};
